import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { BookCover } from "./Books";

const COLORS = ["#FFC72C", "#FF5A36", "#38BDF8", "#34D399", "#A78BFA", "#FDA4AF"];

const emptyBook = {
  title: "",
  description: "",
  age_range: "Ages 3-8",
  price: "",
  cover: "",
  icon: "📖",
  color: "#A78BFA",
  amazon_url: "",
  status: "live",
  rating: "",
  reviews_count: "",
};

export const BooksPanel = () => {
  const [books, setBooks] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    () => api.get("/books").then((r) => setBooks(r.data)).catch(() => setBooks([])),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description,
      age_range: form.age_range,
      price: form.price,
      cover: form.cover,
      icon: form.icon,
      color: form.color,
      amazon_url: form.amazon_url,
      status: form.status,
      rating: parseFloat(form.rating) || 0,
      reviews_count: parseInt(form.reviews_count, 10) || 0,
    };
    try {
      if (form.id) {
        await api.put(`/books/${form.id}`, payload);
        toast.success("Book updated");
      } else {
        await api.post("/books", payload);
        toast.success("Book added — it's live on the site!");
      }
      setForm(null);
      load();
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (book) => {
    if (!window.confirm(`Delete "${book.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/books/${book.id}`);
      toast.success("Book deleted");
      load();
    } catch {
      toast.error("Could not delete. Please try again.");
    }
  };

  const field = (key, label, props = {}) => (
    <div>
      <label className="block text-xs font-extrabold uppercase tracking-widest text-ink/60 mb-1.5">
        {label}
      </label>
      <input
        data-testid={`admin-book-form-${key}-input`}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-4 py-3 rounded-2xl border-2 border-ink bg-cream font-medium focus:outline-none focus:ring-2 focus:ring-coral"
        {...props}
      />
    </div>
  );

  return (
    <section className="mt-14" data-testid="admin-books-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl border-2 border-ink bg-grape/50 flex items-center justify-center">
            <BookOpen size={18} />
          </span>
          <h2 className="font-display font-black text-2xl tracking-tight">Colouring books</h2>
        </div>
        <button
          onClick={() => setForm({ ...emptyBook })}
          data-testid="admin-add-book-button"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-mint text-ink font-display font-extrabold text-sm border-2 border-ink shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal transition-all duration-200"
        >
          <Plus size={16} /> Add a book
        </button>
      </div>

      <div data-testid="admin-books-list" className="mt-6 grid md:grid-cols-2 gap-5">
        {books === null ? (
          <p className="text-ink/50 font-bold">Loading…</p>
        ) : books.length === 0 ? (
          <div className="md:col-span-2 rounded-3xl border-2 border-dashed border-ink/30 bg-white/60 p-12 text-center">
            <p className="font-display font-bold text-xl">No books yet</p>
            <p className="mt-1 text-ink/60">Click "Add a book" to list your first colouring book.</p>
          </div>
        ) : (
          books.map((book) => (
            <div
              key={book.id}
              data-testid={`admin-book-row-${book.id}`}
              className="rounded-3xl border-2 border-ink bg-white shadow-brutal-sm p-5 flex items-start gap-4"
            >
              <div className="w-14 shrink-0">
                <BookCover book={book} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-lg tracking-tight">{book.title}</h3>
                  {book.status !== "live" && (
                    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-grape/25 border border-ink">
                      Coming soon
                    </span>
                  )}
                </div>
                <p className="text-sm text-ink/60 line-clamp-2 mt-0.5">{book.description}</p>
                <p className="text-xs font-bold text-ink/50 mt-1">
                  {book.age_range}
                  {book.price && ` · ${book.price}`}
                  {book.amazon_url ? " · linked to Amazon" : " · no Amazon link"}
                  {book.rating > 0 && ` · ★ ${book.rating}`}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() =>
                    setForm({
                      ...book,
                      rating: book.rating || "",
                      reviews_count: book.reviews_count || "",
                    })
                  }
                  data-testid={`admin-edit-book-button-${book.id}`}
                  className="p-2.5 rounded-xl bg-skykit/20 border-2 border-ink hover:bg-skykit/40 transition-colors"
                  aria-label={`Edit ${book.title}`}
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => remove(book)}
                  data-testid={`admin-delete-book-button-${book.id}`}
                  className="p-2.5 rounded-xl bg-coral/15 border-2 border-ink hover:bg-coral/30 transition-colors"
                  aria-label={`Delete ${book.title}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div
            data-testid="admin-book-form"
            className="w-full max-w-2xl rounded-[2rem] border-2 border-ink bg-white shadow-brutal-lg p-6 sm:p-8 my-8"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display font-black text-2xl tracking-tight">
                {form.id ? "Edit book" : "Add a new book"}
              </h2>
              <button
                onClick={() => setForm(null)}
                data-testid="admin-book-form-close-button"
                className="p-2 rounded-full border-2 border-ink hover:bg-ink/5"
                aria-label="Close form"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={save} className="mt-6 grid sm:grid-cols-2 gap-4">
              {field("title", "Book title", { required: true, placeholder: "Cute Halloween Colouring Book" })}
              {field("age_range", "Age range", { placeholder: "Ages 3-8" })}
              <div className="sm:col-span-2">
                <label className="block text-xs font-extrabold uppercase tracking-widest text-ink/60 mb-1.5">
                  Description
                </label>
                <textarea
                  data-testid="admin-book-form-description-input"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-2xl border-2 border-ink bg-cream font-medium focus:outline-none focus:ring-2 focus:ring-coral"
                  placeholder="30 big and easy pages…"
                />
              </div>
              {field("price", "Price (optional)", { placeholder: "£7.99" })}
              {field("amazon_url", "Amazon link", { placeholder: "https://www.amazon.co.uk/dp/…" })}
              {field("rating", "Amazon star rating (0-5, optional)", {
                type: "number",
                min: "0",
                max: "5",
                step: "0.1",
                placeholder: "e.g. 4.8 — stars show once above 0",
              })}
              {field("reviews_count", "Number of Amazon reviews (optional)", {
                type: "number",
                min: "0",
                step: "1",
                placeholder: "e.g. 12",
              })}
              <div className="sm:col-span-2">
                {field("cover", "Cover image path or URL", { placeholder: "/books/christmas.jpg or https://…" })}
              </div>
              {field("icon", "Fallback icon (emoji, shown if no cover)", { placeholder: "🦕" })}
              <div>
                <label className="block text-xs font-extrabold uppercase tracking-widest text-ink/60 mb-1.5">
                  Status
                </label>
                <select
                  data-testid="admin-book-form-status-select"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full px-4 py-3 rounded-2xl border-2 border-ink bg-cream font-medium focus:outline-none focus:ring-2 focus:ring-coral"
                >
                  <option value="live">Live on Amazon</option>
                  <option value="coming">Coming soon</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-extrabold uppercase tracking-widest text-ink/60 mb-1.5">
                  Placeholder color
                </label>
                <div className="flex gap-2 flex-wrap pt-1">
                  {COLORS.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-9 h-9 rounded-xl border-2 border-ink transition-transform ${
                        form.color === c ? "scale-110 ring-2 ring-ink ring-offset-2" : ""
                      }`}
                      style={{ backgroundColor: c }}
                      aria-label={`Color ${c}`}
                    />
                  ))}
                </div>
              </div>

              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button
                  type="submit"
                  data-testid="admin-book-form-submit-button"
                  disabled={saving}
                  className="flex-1 px-6 py-4 rounded-2xl bg-ink text-cream font-display font-extrabold text-lg border-2 border-ink shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal hover:bg-coral transition-all duration-200 disabled:opacity-60"
                >
                  {saving ? "Saving…" : form.id ? "Save changes" : "Publish book"}
                </button>
                <button
                  type="button"
                  onClick={() => setForm(null)}
                  className="px-6 py-4 rounded-2xl bg-white font-display font-extrabold text-lg border-2 border-ink hover:bg-ink/5 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};
